import React from 'react';
import {
  IconButton,
  Switch,
  FormControlLabel,
  Tooltip,
  Box,
  Typography,
} from '@mui/material';
import {
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Brightness6 as BrightnessIcon,
} from '@mui/icons-material';
import { useDomainTheme } from '../../contexts/DomainThemeContext';

export interface DarkModeToggleProps {
  variant?: 'icon' | 'switch' | 'compact';
  size?: 'small' | 'medium' | 'large';
  showLabel?: boolean;
  color?: 'inherit' | 'primary' | 'secondary' | 'default';
}

/**
 * Dark Mode Toggle Component
 * 
 * Provides a toggle control for switching between light and dark themes
 * with smooth transitions and proper accessibility support.
 * 
 * Variants:
 * - icon: Simple icon button toggle
 * - switch: Material switch with label
 * - compact: Icon with indicator
 */
export const DarkModeToggle: React.FC<DarkModeToggleProps> = ({
  variant = 'icon',
  size = 'medium',
  showLabel = false,
  color = 'inherit',
}) => {
  const { darkMode, toggleDarkMode } = useDomainTheme();

  const iconSize = size === 'small' ? 18 : size === 'large' ? 24 : 20;

  if (variant === 'switch') {
    return (
      <FormControlLabel
        control={
          <Switch
            checked={darkMode}
            onChange={toggleDarkMode}
            size={size}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': {
                color: 'primary.main',
              },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                backgroundColor: 'primary.main',
                opacity: 0.5,
              },
            }}
          />
        }
        label={
          showLabel ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {darkMode ? (
                <DarkModeIcon sx={{ fontSize: iconSize }} />
              ) : (
                <LightModeIcon sx={{ fontSize: iconSize }} />
              )}
              <Typography variant="body2">
                {darkMode ? 'Dark Mode' : 'Light Mode'}
              </Typography>
            </Box>
          ) : undefined
        }
        sx={{
          margin: 0,
          '& .MuiFormControlLabel-label': {
            fontSize: size === 'small' ? 12 : 14,
          },
        }}
      />
    );
  }

  if (variant === 'compact') {
    return (
      <Tooltip title={`Switch to ${darkMode ? 'light' : 'dark'} mode`}>
        <Box
          onClick={toggleDarkMode}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 1,
            borderRadius: 1,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          <BrightnessIcon sx={{ fontSize: iconSize, color: 'text.secondary' }} />
          {showLabel && (
            <Typography variant="caption" sx={{ fontSize: 12 }}>
              {darkMode ? 'Dark' : 'Light'}
            </Typography>
          )}
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: darkMode ? 'warning.main' : 'primary.main',
              transition: 'background-color 0.2s ease',
            }}
          />
        </Box>
      </Tooltip>
    );
  }

  // Default icon variant
  return (
    <Tooltip title={`Switch to ${darkMode ? 'light' : 'dark'} mode`}>
      <IconButton
        onClick={toggleDarkMode}
        color={color}
        size={size}
        sx={{
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        }}
      >
        {darkMode ? (
          <LightModeIcon sx={{ fontSize: iconSize }} />
        ) : (
          <DarkModeIcon sx={{ fontSize: iconSize }} />
        )}
      </IconButton>
    </Tooltip>
  );
};

export default DarkModeToggle;